import type ActionButton from '@lved/ui/components/shared/ui/ActionButton';
import type PreviewComponentType from '@lved/ui/components/shared/ui/Preview';
import type { AppShell } from '@theia-lved-core/browser/shell/application-shell';
import { ReactWidget, ApplicationShell as TheiaApplicationShell } from '@theia/core/lib/browser';
import { inject, injectable, postConstruct } from '@theia/core/shared/inversify';

type PreviewComponent = typeof PreviewComponentType;
type ActionButtonComponent = typeof ActionButton;
type PreviewModule = {
    Preview: PreviewComponent;
    ActionButton: ActionButtonComponent;
};

@injectable()
export class PreviewWidget extends ReactWidget {
    static readonly ID: string = 'widget:lvgl-preview';
    static readonly LABEL: string = 'Preview';

    protected previewComponent: PreviewComponent | undefined;
    protected previewError: string | undefined;
    protected previewModule: PreviewModule | undefined;

    @inject(TheiaApplicationShell)
    protected readonly shell!: TheiaApplicationShell;

    @postConstruct()
    protected init(): void {
        this.doInit();
    }

    protected async doInit(): Promise<void> {
        this.id = PreviewWidget.ID;
        this.title.label = PreviewWidget.LABEL;
        this.title.caption = PreviewWidget.LABEL;
        this.title.closable = true;
        this.title.iconClass = 'codicon codicon-eye';
        await this.loadPreviewComponent();
        this.update();
    }

    protected async loadPreviewComponent(): Promise<void> {
        try {
            if (!this.hasSupportedPlatformApi()) {
                this.previewError =
                    'Preview requires an LVED Platform bridge. Theia does not provide one yet, so @lved/ui Preview cannot run here directly.';
                this.update();
                return;
            }

            const module: PreviewModule = (await import('@lved/ui')) as unknown as PreviewModule;
            this.previewModule = module;
            this.previewComponent = module.Preview;
            this.previewError = undefined;
        } catch (error: unknown) {
            this.previewError = error instanceof Error ? error.message : 'Failed to load @lved/ui Preview.';
        }
    }

    protected hasSupportedPlatformApi(): boolean {
        return Boolean((window as Window & { _platformApi?: unknown })._platformApi);
    }

    protected handleToggleCodeClick(): void {
        const lvglShell = this.shell as AppShell;
        const isVisible = lvglShell.isMainPanelVisible();
        lvglShell.setMainPanelVisible(!isVisible);
        document.body.setAttribute('data-lvgl-main', isVisible ? 'hidden' : 'visible');
        this.update();
    }

    protected render(): JSX.Element {
        const Preview: PreviewComponent | undefined = this.previewComponent;
        const ActionButton: ActionButtonComponent | undefined = this.previewModule?.ActionButton;
        const codeVisible = (this.shell as AppShell).isMainPanelVisible();
        const toggleButton = ActionButton ? (
            <ActionButton
                variant="tertiary"
                icon="code"
                iconSize={16}
                isSelected={codeVisible}
                onClick={() => this.handleToggleCodeClick()}
                title={codeVisible ? 'Hide code editor' : 'Show code editor'}
            />
        ) : undefined;

        return (
            <div className="lvgl-preview-wrapper">
                {Preview ? (
                    <Preview className="lvgl-preview" extraDesignActions={toggleButton} />
                ) : (
                    <p>{this.previewError ?? 'Loading LVED Preview...'}</p>
                )}
            </div>
        );
    }
}
